import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { RecadastramentoContext } from "@contexts";
import { RecadastramentoService } from "@services";
import { UFEntidade, WebRecadParticipanteDadosEntidade } from "@entidades";
import { ArquivoUpload, LoadingComponent } from "@components";
import { Alert, Button, Col, ComboBox, Form, Input, Row } from "@intech/web-ui";
import { FaAngleDoubleLeft, FaAngleDoubleRight } from "react-icons/fa";
import { ArquivoUploadItem } from "@src/components/ArquivoUpload";
import { HouveAlteracao, StringIsNullOrEmpty } from "@helpers";

interface IProps {
  avancar: (PassoEspecifico?: number | null) => Promise<void>;
  voltar: (PassoEspecifico?: number | null) => Promise<void>;
}

export const Passo2: React.FC<IProps> = ({
  avancar,
  voltar
}) => {
  const context = useContext(RecadastramentoContext);
  const isMountedRef = useRef(true);
  const form = useRef<Form>(null);

  const [ListaArquivo, setListaArquivo] = useState<Array<ArquivoUploadItem>>(new Array<ArquivoUploadItem>());
  const [Nome, setNome] = useState<string>("");
  const [DataEmissao, setDataEmissao] = useState<string>("");
  const [OrgaoEmissor, setOrgaoEmissor] = useState<string>("");
  const [Naturalidade, setNaturalidade] = useState<string>("");
  const [UfNaturalidade, setUfNaturalidade] = useState<string>("");
  const [ListaUF, setListaUF] = useState<UFEntidade[]>([]);
  const [DadosOriginais, setDadosOriginais] = useState<WebRecadParticipanteDadosEntidade>(null);

  const [Loading, setLoading] = useState<boolean>(true);
  const [Erro, setErro] = useState<string>("");
  const [ErroForm, setErroForm] = useState<string>("");

  useEffect(() => () => { isMountedRef.current = false }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        if (!isMountedRef.current)
          return;

        const dados = await context.buscarDadosParticipante();
        const dadosOriginais = await context.buscarDadosOriginais();

        const ListaUF = await RecadastramentoService.BuscarListaUF();

        const ListaArquivo = await context.buscarListaArquivo();

        setListaArquivo(ListaArquivo);

        setNome(dados.NOME_ENTID || dadosOriginais.NOME_ENTID || "");
        setDataEmissao(dados.DT_EMIS_IDENT || dadosOriginais.DT_EMIS_IDENT || "");
        setOrgaoEmissor(dados.ORG_EMIS_IDENT || dadosOriginais.ORG_EMIS_IDENT || "");
        setNaturalidade(dados.NATURALIDADE || dadosOriginais.NATURALIDADE || "");
        setUfNaturalidade(dados.UF_NATURALIDADE || dadosOriginais.UF_NATURALIDADE || "");
        setListaUF(ListaUF);
        setDadosOriginais(dadosOriginais);
      } catch (e) {
        const error: any = e;
        const messsage = error.response ? error.response.data : error;
        setErro(messsage);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const PrecisaEnviarDocumento = useCallback(() => {
    try {
      if (!isMountedRef.current || !DadosOriginais)
        return;

      const houveAlteracao = (
        HouveAlteracao(DadosOriginais.NOME_ENTID, Nome) ||
        HouveAlteracao(DadosOriginais.DT_EMIS_IDENT, DataEmissao) ||
        HouveAlteracao(DadosOriginais.ORG_EMIS_IDENT, OrgaoEmissor) ||
        HouveAlteracao(DadosOriginais.NATURALIDADE, Naturalidade) ||
        HouveAlteracao(DadosOriginais.UF_NATURALIDADE, UfNaturalidade)
      );

      return (houveAlteracao && ListaArquivo.filter(arquivo => arquivo.Passo === ("Passo2")).length === 0);
    }
    catch (err) {
      setErro(err["response"] ? err["response"]["data"] : err);
    }
  }, [Nome, DataEmissao, OrgaoEmissor, Naturalidade, UfNaturalidade, ListaArquivo, DadosOriginais]);

  async function handleVoltar() {
    form?.current?.clear();
    setErroForm("");
    try {
      if (await salvarDados()) {
        voltar();
      }
    }
    catch (e) {
      const error: any = e;
      const messsage = error.response ? error.response.data : error;
      setErroForm(messsage);
    }
  }

  async function handleAvancar() {
    form?.current.clear();
    setErroForm("");
    try {
      if (await salvarDados()) {
        if (StringIsNullOrEmpty(UfNaturalidade)) {
          setErroForm("Campo Naturalidade - Estado obrigatório.");
          return;
        }

        if (PrecisaEnviarDocumento()) {
          setErroForm("O envio de pelo menos um documento é obrigatório.");
          return;
        }

        avancar();
      }
    }
    catch (e) {
      const error: any = e;
      const messsage = error.response ? error.response.data : error;
      setErroForm(messsage);
    }
  }

  async function salvarDados() {
    try {
      if (form?.current?.validate()) {

        const dataLimpa = StringIsNullOrEmpty(DataEmissao) ? "" : DataEmissao.replaceAll("/", ".");
        await RecadastramentoService.ValidarDataEmissao(dataLimpa);

        const dados = await context.buscarDadosParticipante();
        dados.NOME_ENTID = Nome;
        dados.DT_EMIS_IDENT = DataEmissao;
        dados.ORG_EMIS_IDENT = OrgaoEmissor;
        dados.NATURALIDADE = Naturalidade;
        dados.UF_NATURALIDADE = UfNaturalidade;

        context.salvarDadosParticipante(dados);
        context.salvarListaArquivo(ListaArquivo);
        return true;
      }
      return false;
    }
    catch (e) {
      const error: any = e;
      const messsage = error.response ? error.response.data : error;
      setErroForm(messsage);
      return false;
    }
  }

  return (
    <>
      {Loading && <LoadingComponent />}

      {!Loading && Erro && <Alert type={'danger'}>{Erro}</Alert>}

      {!Loading && !Erro && (
        <>
          <Form ref={form}>
            <Input
              value={Nome}
              onChange={setNome}
              title={"Nome"}
              max={60}
              required
            />

            <Input
              value={DadosOriginais.DT_NASCIMENTO || ""}
              title={"Data de Nascimento"}
              readonly
            />

            <Input
              value={DadosOriginais.CPF_CGC || ""}
              title={"CPF"}
              mask={"cpf"}
              readonly
            />

            <Input
              value={DadosOriginais.NU_IDENT || ""}
              title={"RG"}
              readonly
            />

            <Input
              value={DataEmissao}
              onChange={setDataEmissao}
              title={"Data de Emissão"}
              type={"date"}
              required
            />

            <Input
              value={OrgaoEmissor}
              onChange={setOrgaoEmissor}
              title={"Orgão Emissor"}
              max={8}
              required
            />

            <Input
              value={Naturalidade}
              onChange={setNaturalidade}
              title={"Naturalidade"}
              max={25}
              required
            />

            <ComboBox
              data={ListaUF}
              title={"* Naturalidade - Estado"}
              value={UfNaturalidade}
              onChange={setUfNaturalidade}
              memberName={"DS_UNID_FED"}
              memberValue={"CD_UNID_FED"}
            />

            <Input
              value={DadosOriginais.NOME_MAE || ""}
              title={"Nome da Mãe"}
              readonly
            />

            <Input
              value={DadosOriginais.NOME_PAI || ""}
              title={"Nome do Pai"}
              readonly
            />

            <Alert formAlert type={"danger"} />
            <Alert type={"danger"} >{ErroForm}</Alert>
          </Form>

          <ArquivoUpload
            ListaArquivo={ListaArquivo}
            setListaArquivo={setListaArquivo}
            texto={"Em caso de alteração de qualquer dado, por favor, anexar cópia do novo RG."}
            passo={"Passo2"}
            controller={"Recadastramento"}
          />

          <Row>
            <Col>
              <Button
                type={"secondary"}
                title={"Voltar"}
                icon={<FaAngleDoubleLeft />}
                onClick={handleVoltar}
                loadingText={"Voltando..."}
                usesLoading
                block
                iconRight
              />
            </Col>

            <Col>
              <Button
                type={"primary"}
                title={"Continuar"}
                icon={<FaAngleDoubleRight />}
                onClick={handleAvancar}
                loadingText={"Continuando..."}
                usesLoading
                block
              />
            </Col>
          </Row>
        </>
      )}
    </>
  );
}